import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { MachineBreakdown } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface TopMachinesChartProps {
  topMachines: MachineBreakdown[];
}

const TopMachinesChart: React.FC<TopMachinesChartProps> = ({ topMachines }) => {
  const { translate } = useTranslation("operations");

  const labels = topMachines.map(m => {
    const name = m.machineName;
    return name.length > 25 ? name.substring(0, 22) + "..." : name;
  });

  const data = {
    labels,
    datasets: [
      {
        label: translate("analytics.totalCreated"),
        data: topMachines.map(m => m.totalCount - m.overdueCount),
        backgroundColor: "rgba(52, 152, 219, 0.7)",
        borderRadius: 4,
        maxBarThickness: 20,
      },
      {
        label: translate("analytics.overdue"),
        data: topMachines.map(m => m.overdueCount),
        backgroundColor: "rgba(239, 68, 68, 0.7)",
        borderRadius: 4,
        maxBarThickness: 20,
      },
    ],
  };

  const options = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          font: { family: "var(--font-sans)", size: 12 },
          color: dt.textSecondary,
          usePointStyle: true,
          pointStyle: "rectRounded" as const,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: "#1a1a2e",
        titleFont: { family: "var(--font-sans)", size: 13 },
        bodyFont: { family: "var(--font-mono)", size: 12 },
        cornerRadius: 8,
        padding: 10,
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          font: { family: "var(--font-mono)", size: 11 },
          color: dt.textMuted,
          stepSize: 1,
        },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: "var(--font-sans)", size: 12 },
          color: dt.textSecondary,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        p: 2.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 600,
          color: dt.textPrimary,
          mb: 2,
        }}
      >
        {translate("analytics.topMachines")}
      </Typography>
      <Box sx={{ height: 280 }}>
        {topMachines.length > 0 ? (
          <Bar data={data} options={options} />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
              {translate("analytics.noData")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TopMachinesChart;
